import React, { useEffect, useState, Suspense } from 'react';
import { RoleEditItemContainerStyled, EditBarStyled } from './styled';
import {
  Text,
  Box,
  Icon,
  IconSize,
  FormControl,
  TextField,
  ButtonAppearance,
  Button,
  TextFieldSize,
  Spinner,
  SpinnerSize,
  theme,
} from '@bechtel/gds';
import { AuthRole, UpdateRolePayload, UpdateRoleInput, UpdateRoleError } from '@bechtel/types';
import { Formik, Form, Field, FormikProps } from 'formik';
import { object, string } from 'yup';
import { UPDATE_ROLE } from '@bechtel/client-gql';
import { get } from 'lodash';
import { ApolloError, useMutation } from '@apollo/client';
import { ErrorBoundary, SuspenseLoader } from '@bechtel/client-common';
import { RoleError } from '../../error';
import { t, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';

interface RoleEditItemProps {
  item: AuthRole;
  setShowEditItem: React.Dispatch<React.SetStateAction<boolean>>;
}

interface FormikEditRoleState {
  name: AuthRole['name'];
  abbreviation: AuthRole['abbreviation'];
}

const roleNameValidationSchema = object().shape({
  name: string()
    .trim()
    .min(1, t`Please enter atleast one character for name`)
    .max(255, t`Please limit name to 255 characters`)
    .required(t`Name is mandatory`),
  abbreviation: string()
    .nullable()
    .max(8, t`Please limit abbreviation to 8 characters`),
});

let inititalRoleValues: FormikEditRoleState = {
  name: '',
  abbreviation: '',
};

export const EditRole: React.FC<RoleEditItemProps> = ({ item, setShowEditItem }) => {
  const { name, abbreviation, numUsers } = item;
  const { i18n } = useLingui();
  inititalRoleValues = { ...inititalRoleValues, name: name, abbreviation: !abbreviation ? '' : abbreviation };

  const [updateRole, { data, loading, error }] =
    useMutation<{ updateRole: UpdateRolePayload }, { input: UpdateRoleInput }>(UPDATE_ROLE);
  const updateRoleError = get(data, 'updateRole.error') || error;
  const [roleError, setRoleError] = useState<UpdateRoleError | ApolloError | null>(null);

  useEffect(() => {
    if (!updateRoleError) return;
    setRoleError(updateRoleError);
  }, [updateRoleError]);

  return (
    <>
      <Formik
        initialValues={inititalRoleValues}
        validationSchema={roleNameValidationSchema}
        onSubmit={async (values: FormikEditRoleState) => {
          await updateRole({
            variables: {
              input: {
                id: item.id,
                name: values.name,
                abbreviation: values.abbreviation,
              },
            },
          });
          setShowEditItem(false);
        }}
      >
        {({ submitForm, dirty, errors, isValid }: FormikProps<FormikEditRoleState>) => (
          <Form>
            <RoleEditItemContainerStyled data-testid="edit-container">
              <EditBarStyled />
              <Box
                justifyContent={{ sm: 'space-between' }}
                alignItems={{ sm: 'center' }}
                mt={{ sm: '400' }}
                mb={{ sm: '700' }}
              >
                <Box flexDirection={{ sm: 'column' }}>
                  <Text family="graphik" size="300" color="grey.1000">
                    <Trans>Editing:</Trans>
                  </Text>
                  <Text family="graphik" size="300" color="grey.1000" style={{ marginTop: `${theme.spacings[400]}` }}>
                    {name}
                  </Text>
                </Box>
                <Box justifyContent={{ sm: 'flex-end' }} alignItems={{ sm: 'center' }}>
                  <Box mr={{ sm: '700' }}>
                    <Text color="blue.700" size="300">
                      {numUsers ? numUsers : 0} <Trans>users</Trans>
                    </Text>
                  </Box>
                  <Box mr={{ sm: '500' }} style={{ opacity: 0.4, pointerEvents: 'none' }}>
                    <Icon icon="download" size={IconSize.MEDIUM} fillColor="blue.800"></Icon>
                  </Box>
                </Box>
              </Box>
              <Box>
                <Box mr={{ sm: '500' }} flexBasis={{ sm: '45%' }} flexDirection={{ sm: 'column' }}>
                  <FormControl
                    style={{ flexGrow: 1 }}
                    label={t`Name (Required)`}
                    css={`
                      width: 100%;
                    `}
                  >
                    <Field name="name">
                      {({ field }) => (
                        <TextField
                          size={TextFieldSize.LARGE}
                          maxLength={255}
                          {...field}
                          data-testid="name-input"
                        ></TextField>
                      )}
                    </Field>
                  </FormControl>
                  {errors && errors.name && (
                    <Box mt={{ sm: '300' }}>
                      <Text size="400" weight="300" color="bechtel.red">
                        {i18n._(errors?.name)}
                      </Text>
                    </Box>
                  )}
                </Box>
                <Box mr={{ sm: '500' }} flexBasis={{ sm: '25%' }} flexDirection={{ sm: 'column' }}>
                  <FormControl
                    style={{ flexGrow: 1 }}
                    label={t`Abbreviation`}
                    css={`
                      width: 100%;
                    `}
                  >
                    <Field name="abbreviation">
                      {({ field }) => (
                        <TextField
                          size={TextFieldSize.LARGE}
                          maxLength={8}
                          {...field}
                          style={{ textTransform: 'uppercase' }}
                          data-testid="abbreviation-input"
                        ></TextField>
                      )}
                    </Field>
                  </FormControl>
                  {errors && errors.abbreviation && (
                    <Box mt={{ sm: '300' }}>
                      <Text size="400" weight="300" color="bechtel.red">
                        {i18n._(errors?.abbreviation)}
                      </Text>
                    </Box>
                  )}
                </Box>
              </Box>
              <Box alignSelf={{ sm: 'flex-end' }} style={{ marginTop: 'auto' }}>
                <Box mr={{ sm: '400' }}>
                  <Button
                    appearance={ButtonAppearance.TERTIARY}
                    onClick={() => setShowEditItem(false)}
                    data-testid="edit-cancel-btn"
                  >
                    <Text color="blue.800" size="400" weight="600">
                      <Trans>Cancel</Trans>
                    </Text>
                  </Button>
                </Box>
                <Button
                  appearance={ButtonAppearance.TERTIARY}
                  onClick={submitForm}
                  disabled={!dirty || !isValid || loading}
                  data-testid="edit-save-btn"
                >
                  {loading && <Spinner size={SpinnerSize.SMALL}></Spinner>}
                  <Text color="blue.800" size="400" weight="600">
                    <Trans>Save</Trans>
                  </Text>
                </Button>
              </Box>
            </RoleEditItemContainerStyled>
          </Form>
        )}
      </Formik>
      {roleError && (
        <ErrorBoundary>
          <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
            <RoleError roleError={roleError} onClose={() => setRoleError(null)}></RoleError>
          </Suspense>
        </ErrorBoundary>
      )}
    </>
  );
};
